import { node } from "prop-types";
import { useLayoutEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useAutocompleteContext } from "../fields/AutocompleteContext";

/**
 * Offsets that seat the corner with an equal gap on all three exposed edges
 * (top, right, bottom) of the street input's box. The gap is half the leftover
 * vertical space — so centering the corner vertically and insetting it from the
 * input's right edge by that same amount lands top === right === bottom.
 *
 * All rects are `getBoundingClientRect()` results; `parentBorder*` are the
 * positioning parent's border widths (the corner is absolute, so its offsets
 * are measured from the parent's content box, not its border edge).
 */
export function centerInInputCorner({
  inputRect,
  parentRect,
  cornerRect,
  parentBorderTop = 0,
  parentBorderRight = 0,
}) {
  const gap = (inputRect.height - cornerRect.height) / 2;
  return {
    top: inputRect.top - parentRect.top - parentBorderTop + gap,
    right: parentRect.right - parentBorderRight - inputRect.right + gap,
  };
}

/**
 * Seats the country flag in the street input's right corner, vertically
 * centered with an equal gap top/right/bottom — by measuring the input's box
 * rather than carrying per-surface magic offsets (the old `bottom: 9px` was
 * calibrated to one flag size and drifted when the flag changed). The street
 * input rides the autocomplete context as `addr1Ref`.
 *
 * Must render inside the consumer's `position: relative` Line1 (its
 * `offsetParent`) — the same slot the absolute wrapper occupied before.
 */
export default function CountryFlagCorner({ children }) {
  const { addr1Ref } = useAutocompleteContext();
  const cornerRef = useRef(null);
  const [offsets, setOffsets] = useState(null);
  // Last applied offsets, so the per-frame settle loop only re-renders when the
  // corner actually needs to move (sub-pixel jitter is ignored).
  const appliedRef = useRef(null);

  useLayoutEffect(() => {
    const input = addr1Ref?.current;
    const corner = cornerRef.current;
    const parent = corner?.offsetParent;
    if (!input || !corner || !parent) return;

    // `force` bypasses the sub-pixel dedupe so the final settle frame locks the
    // exact resting offset — the open animation overshoots its resting box by a
    // fraction of a pixel, and that small settle-back would otherwise fall under
    // the dedupe threshold and leave the flag a hair off-center.
    const measure = (force = false) => {
      const parentStyle = window.getComputedStyle(parent);
      const next = centerInInputCorner({
        inputRect: input.getBoundingClientRect(),
        parentRect: parent.getBoundingClientRect(),
        cornerRect: corner.getBoundingClientRect(),
        parentBorderTop: parseFloat(parentStyle.borderTopWidth) || 0,
        parentBorderRight: parseFloat(parentStyle.borderRightWidth) || 0,
      });
      const prev = appliedRef.current;
      if (
        !force &&
        prev &&
        Math.abs(prev.top - next.top) < 0.25 &&
        Math.abs(prev.right - next.right) < 0.25
      ) {
        return;
      }
      appliedRef.current = next;
      setOffsets(next);
    };
    measure();

    // The synchronous measure above runs pre-paint. On a COLD first open the
    // popover's open animation drifts the street input's POSITION within the
    // parent over ~300ms (it eases from a transient spot into its resting box),
    // so a single measure seats the flag ~1.6px high/right and it never
    // corrects — the ResizeObserver below misses a position-only shift (the
    // input's SIZE never changes). Re-measure every frame through the open
    // window so the flag tracks the input and lands on its settled box. Warm
    // re-opens settle on frame one, so the loop just confirms the same value
    // (no re-render, per the dedupe above) and stops at the cap.
    let rafId;
    const canRaf =
      typeof requestAnimationFrame !== "undefined" &&
      typeof performance !== "undefined";
    if (canRaf) {
      const settleUntil = performance.now() + 600;
      const tick = () => {
        if (performance.now() < settleUntil) {
          measure();
          rafId = requestAnimationFrame(tick);
        } else {
          measure(true);
        }
      };
      rafId = requestAnimationFrame(tick);
    }

    // Re-center when the input reflows — responsive width, font load, zoom.
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => measure())
        : null;
    if (observer) {
      observer.observe(input);
      observer.observe(parent);
    }
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [addr1Ref]);

  return (
    <Corner ref={cornerRef} style={offsets ?? undefined}>
      {children}
    </Corner>
  );
}

CountryFlagCorner.propTypes = {
  children: node,
};

// Shrink-wraps the flag (inline-flex) so the measured corner rect IS the flag's
// box. Absolutely positioned within Line1; `top`/`right` arrive measured. The
// pre-measure fallback parks it in the input's right corner so it never flashes
// elsewhere before the layout effect (which runs pre-paint) sets the offsets.
const Corner = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  /* Above the focused Street field's wrapper (z-index 1) so focusing the input
     never stacks over and covers the flag nested in its corner. */
  z-index: 2;
`;
